import React, { useEffect, useMemo, useState } from 'react';
import { IoCalendarClearOutline, IoChevronBack, IoChevronForward, IoClose } from 'react-icons/io5';
import { format } from 'date-fns';

interface DatePickerProps {
  value: string | null;
  onChange: (date: string | null) => void;
  className?: string;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, className = '' }) => {
  const parsedValue = useMemo(() => (value ? parseLocalDate(value) : null), [value]);
  const today = useMemo(() => new Date(), []);
  const [visibleYear, setVisibleYear] = useState((parsedValue ?? today).getFullYear());
  const [visibleMonth, setVisibleMonth] = useState((parsedValue ?? today).getMonth());

  useEffect(() => {
    if (!parsedValue) return;
    setVisibleYear(parsedValue.getFullYear());
    setVisibleMonth(parsedValue.getMonth());
  }, [parsedValue]);

  const years = useMemo(() => {
    const currentYear = today.getFullYear();
    const start = currentYear - 25;
    return Array.from({ length: 31 }, (_, index) => start + index);
  }, [today]);

  const days = useMemo(() => {
    const firstDay = new Date(visibleYear, visibleMonth, 1);
    const lastDay = new Date(visibleYear, visibleMonth + 1, 0);
    const cells: (Date | null)[] = [];

    for (let index = 0; index < firstDay.getDay(); index += 1) {
      cells.push(null);
    }

    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      cells.push(new Date(visibleYear, visibleMonth, day));
    }

    return cells;
  }, [visibleMonth, visibleYear]);

  const selectedLabel = parsedValue ? format(parsedValue, 'MMM dd, yyyy') : 'All messages';

  const shiftMonth = (delta: number) => {
    const next = new Date(visibleYear, visibleMonth + delta, 1);
    setVisibleYear(next.getFullYear());
    setVisibleMonth(next.getMonth());
  };

  const chooseDate = (date: Date) => {
    onChange(format(date, 'yyyy-MM-dd'));
  };

  const chooseToday = () => {
    setVisibleYear(today.getFullYear());
    setVisibleMonth(today.getMonth());
    chooseDate(today);
  };

  return (
    <div className={`w-[min(22rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-cyan-300/25 bg-[#061b1c]/98 text-slate-100 shadow-2xl shadow-cyan-950/60 backdrop-blur-xl ${className}`}>
      <div className="bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.28),transparent_40%),linear-gradient(135deg,rgba(20,184,166,0.24),rgba(59,130,246,0.16))] px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100/70">
              <IoCalendarClearOutline size={15} />
              Filter Date
            </div>
            <div className="mt-1 text-lg font-semibold text-white">{selectedLabel}</div>
          </div>
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="rounded-full border border-white/15 bg-white/10 p-2 text-cyan-50 transition hover:bg-white/20"
              title="Clear date filter"
            >
              <IoClose size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3 p-4">
        <div className="grid grid-cols-[2.5rem_1fr_1fr_2.5rem] gap-2">
          <button
            type="button"
            onClick={() => shiftMonth(-1)}
            className="flex h-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-cyan-100 transition hover:bg-cyan-300/15"
            title="Previous month"
          >
            <IoChevronBack size={18} />
          </button>
          <select
            value={visibleMonth}
            onChange={(event) => setVisibleMonth(Number(event.target.value))}
            className="h-10 rounded-xl border border-cyan-300/20 bg-[#0b2a2c] px-3 text-sm font-semibold text-white outline-none focus:border-cyan-300"
            aria-label="Select month"
          >
            {MONTHS.map((month, index) => (
              <option key={month} value={index}>
                {month}
              </option>
            ))}
          </select>
          <select
            value={visibleYear}
            onChange={(event) => setVisibleYear(Number(event.target.value))}
            className="h-10 rounded-xl border border-cyan-300/20 bg-[#0b2a2c] px-3 text-sm font-semibold text-white outline-none focus:border-cyan-300"
            aria-label="Select year"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => shiftMonth(1)}
            className="flex h-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-cyan-100 transition hover:bg-cyan-300/15"
            title="Next month"
          >
            <IoChevronForward size={18} />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {WEEK_DAYS.map((day) => (
            <div key={day} className="py-1 text-center text-[11px] font-bold uppercase text-cyan-100/55">
              {day}
            </div>
          ))}
          {days.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }

            const selected = parsedValue ? isSameDay(date, parsedValue) : false;
            const current = isSameDay(date, today);

            return (
              <button
                key={date.toISOString()}
                type="button"
                onClick={() => chooseDate(date)}
                className={`aspect-square rounded-xl text-sm font-semibold transition ${
                  selected
                    ? 'bg-cyan-400 text-[#042326] shadow-lg shadow-cyan-400/25'
                    : current
                    ? 'border border-cyan-300/55 bg-cyan-300/10 text-cyan-100'
                    : 'text-slate-200 hover:bg-white/10 hover:text-white'
                }`}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-2 border-t border-white/10 pt-3">
          <button
            type="button"
            onClick={chooseToday}
            className="rounded-xl bg-cyan-400 px-3 py-2 text-sm font-bold text-[#042326] transition hover:bg-cyan-300"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-cyan-50 transition hover:bg-white/10"
          >
            Show all
          </button>
        </div>
      </div>
    </div>
  );
};

function parseLocalDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function isSameDay(left: Date, right: Date) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

export default DatePicker;
